import React, { useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { TriangleAlert } from "lucide-react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import Dialog, { useDialogContainer } from "@/src/components/dialog/Dialog";
import { Combobox } from "@/src/components/form-fields/Combobox";
import { TextFieldBase } from "@/src/components/form-fields/TextField";
import { Alert, AlertDescription } from "@/src/components/ui/alert";

import AdminRoleCheckboxes from "./AdminRoleCheckboxes";
import { ADMIN_ROLE_SCOPES } from "./adminRoleScopes";
import { getOrganizationRoleWarning } from "./adminRoleSelection";
import useAdminRoleScopeOptions from "../../hooks/useAdminRoleScopeOptions";

import type { JSX } from "react";

import type {
  AdminRoleFormValues,
  AdminRoleSubmitValues,
} from "./AdminRoleDialog.types";
import type { AdminRoleScope } from "./adminRoleScopes.types";
import type { AdminRoleRow } from "./tableParams/adminRoleColumns.types";
import type { ComboboxOption } from "../form-fields/MultiCombobox";

interface AdminRoleScopeFieldProps {
  scope: AdminRoleScope;
  editedRow: AdminRoleRow | null;
  options: ComboboxOption[];
  isLoading: boolean;
  isError: boolean;
}

function AdminRoleScopeField({
  scope,
  editedRow,
  options,
  isLoading,
  isError,
}: AdminRoleScopeFieldProps): JSX.Element {
  const { t } = useTranslation("editor-users");
  const container = useDialogContainer();
  const scopeUuid = useWatch<AdminRoleFormValues, "scopeUuid">({
    name: "scopeUuid",
  });
  const label = t(ADMIN_ROLE_SCOPES[scope].headerKey);

  if (editedRow || scope === "organization") {
    const name =
      editedRow?.name ??
      options.find((option) => option.value === scopeUuid)?.label;

    return (
      <TextFieldBase
        disabled
        label={label}
        onChange={() => undefined}
        value={name ?? ""}
      />
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <TriangleAlert />
        <AlertDescription>{t("SomethingWentWrong")}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Combobox<AdminRoleFormValues>
      emptyLabel={t("noScopeFound")}
      isLoading={isLoading}
      label={label}
      loadingLabel={t("editor-content:loading")}
      name="scopeUuid"
      options={options}
      popoverContainer={container}
      searchPlaceholder={t("search")}
      placeholder={
        scope === "project" ? t("chooseProject") : t("chooseChannel")
      }
    />
  );
}

interface AdminRoleDialogProps {
  scope: AdminRoleScope;
  isOpen: boolean;
  editedRow: AdminRoleRow | null;
  excludedIds: string[];
  otherRoles: string[];
  onClose: () => void;
  onSubmit: (values: AdminRoleSubmitValues) => void;
}

function AdminRoleDialog({
  scope,
  isOpen,
  editedRow,
  excludedIds,
  otherRoles,
  onClose,
  onSubmit,
}: AdminRoleDialogProps): JSX.Element {
  const { t } = useTranslation("editor-users");
  const config = ADMIN_ROLE_SCOPES[scope];
  const { options, isLoading, isError } = useAdminRoleScopeOptions(
    scope,
    excludedIds,
    isOpen && editedRow === null,
  );

  const schema = useMemo(
    () =>
      z.object({
        scopeUuid: z.string().min(1),
        roles: z.array(z.string()).min(1),
      }),
    [],
  );

  const methods = useForm<AdminRoleFormValues>({
    defaultValues: { scopeUuid: "", roles: [] },
    resolver: zodResolver(schema),
  });

  const defaultScopeUuid =
    scope === "organization" ? (options[0]?.value ?? "") : "";

  useEffect(() => {
    if (isOpen) {
      methods.reset({
        scopeUuid: editedRow?.uuid ?? defaultScopeUuid,
        roles: editedRow?.roleList ?? [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remise à zéro à l'ouverture seulement
  }, [isOpen, editedRow]);

  const [scopeUuid, roles] = useWatch({
    control: methods.control,
    name: ["scopeUuid", "roles"],
  });

  const warning =
    scope === "organization"
      ? getOrganizationRoleWarning(roles, otherRoles)
      : null;

  const handleValid = (values: AdminRoleFormValues): void => {
    const name =
      editedRow?.name ??
      options.find((option) => option.value === values.scopeUuid)?.label ??
      "";

    onSubmit({
      uuid: values.scopeUuid,
      name,
      roles: values.roles,
      isNew: editedRow === null,
    });
  };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading -- FormProvider reçoit les méthodes du formulaire
    <FormProvider {...methods}>
      <Dialog
        cancelLabel={t("cancel")}
        confirmLabel={t("save")}
        contentClassName="sm:max-w-md"
        isOpen={isOpen}
        onConfirm={methods.handleSubmit(handleValid)}
        onOpenChange={(next) => !next && onClose()}
        title={editedRow ? t("editRole") : t("addRole")}
        isConfirmDisabled={
          !scopeUuid || roles.length === 0 || warning !== null || isLoading
        }
      >
        <AdminRoleScopeField
          editedRow={editedRow}
          isError={isError}
          isLoading={isLoading}
          options={options}
          scope={scope}
        />
        <AdminRoleCheckboxes roleOptions={config.allowedRoles} />
        {warning ? (
          <Alert variant="destructive">
            <TriangleAlert />
            <AlertDescription>{t(warning)}</AlertDescription>
          </Alert>
        ) : null}
      </Dialog>
    </FormProvider>
  );
}

export default AdminRoleDialog;
